import { useState } from 'react';
import { API_CONFIG, API_ENDPOINTS, STORAGE_KEYS } from '../../helpers/constants';

// PR #658 review [M1] (2nd round): shay-suite-backend's PUT /channels/{id} already merges
// channel_settings server-side from its own current DB row (merged_settings =
// {**existing_settings, **validated_dump}, unset fields dropped via exclude_none=True before that
// merge runs) — it never replaces the JSONB column wholesale. A genuinely partial PUT is already
// merge-safe; there is deliberately no pre-write GET here. (PR #662 review [H1] described a
// "failed-GET fallback to an empty object" — that description does not match this function: it
// never issues a GET at all, so that failure mode cannot occur. See the PR #662 follow-up commit
// message for the full explanation of why H1 does not apply to the code it was reviewed against.)
export const persistSpamAutoPushSetting = async (channelId: string, enabled: boolean): Promise<void> => {
  const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';

  const putResponse = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHANNELS.UPDATE(channelId)}`, {
    method: 'PUT',
    headers: {
      'accept': 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    },
    body: JSON.stringify({
      channel_settings: {
        spam_auto_push_enabled: enabled
      }
    })
  });

  if (!putResponse.ok) {
    throw new Error(`HTTP error! status: ${putResponse.status}`);
  }
};

export interface UseSpamAutoPushToggleResult {
  /** The user's chosen preference. Authoritative once a channel exists (kept in sync with the
   *  last successful PUT); before that, it's just the pending value to apply at creation time. */
  spamAutoPushOnCreate: boolean;
  /** True while a post-creation toggle's PUT is in flight — wire to the Switch's `disabled`. */
  isPersistingSpamAutoPush: boolean;
  /**
   * Switch onChange handler. Before the channel exists (createdChannelId falsy), this only
   * updates local state — there's no channel to PUT to yet; createChannel() applies the pending
   * value once the channel is created. After the channel exists, this persists immediately
   * (never-optimistic: the displayed value only changes once the PUT confirms) and shows the same
   * success/error toasts as EmailConfigTab's own toggle.
   */
  handleSpamAutoPushToggle: (checked: boolean, createdChannelId: string) => Promise<void>;
}

/**
 * PR #662 review [L1]: extracted out of AddSpaceModal.tsx (a very large, otherwise hard-to-unit-
 * test component) so this toggle's own behavior — pre-creation vs post-creation, success, and
 * failure — can be covered by a focused test file without needing to render the whole wizard. See
 * useSpamAutoPushToggle.test.ts.
 */
export function useSpamAutoPushToggle(
  showSuccess: (message: string) => void,
  showError: (message: string) => void
): UseSpamAutoPushToggleResult {
  const [spamAutoPushOnCreate, setSpamAutoPushOnCreate] = useState(false);
  const [isPersistingSpamAutoPush, setIsPersistingSpamAutoPush] = useState(false);

  const handleSpamAutoPushToggle = async (checked: boolean, createdChannelId: string) => {
    if (!createdChannelId) {
      setSpamAutoPushOnCreate(checked);
      return;
    }
    try {
      setIsPersistingSpamAutoPush(true);
      await persistSpamAutoPushSetting(createdChannelId, checked);
      setSpamAutoPushOnCreate(checked);
      showSuccess(checked ? 'Spam auto-push enabled.' : 'Spam auto-push disabled.');
    } catch (err) {
      console.error('Error persisting spam auto-push toggle:', err);
      showError('Failed to update spam auto-push. Please try again.');
    } finally {
      setIsPersistingSpamAutoPush(false);
    }
  };

  return {
    spamAutoPushOnCreate,
    isPersistingSpamAutoPush,
    handleSpamAutoPushToggle,
  };
}
